/**
 * UTM parameter capture and persistence utility
 * Captures UTM params from URL on initial visit and persists in sessionStorage
 * for attribution in conversion events
 */

export interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

const UTM_STORAGE_KEY = 'jobtrackr_utm_params'
const UTM_COOKIE_NAME = 'jt_utm'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Capture UTM parameters from the current URL and store in sessionStorage
 * Also checks the jt_utm cookie (set by Astro landing pages) as fallback
 * Only captures on first visit (doesn't overwrite existing UTM params in storage)
 */
export const captureUTMParams = (): void => {
  if (typeof window === 'undefined') return

  // Check if we already have UTM params stored (don't overwrite)
  const existingParams = sessionStorage.getItem(UTM_STORAGE_KEY)
  if (existingParams) return

  const urlParams = new URLSearchParams(window.location.search)
  const utmParams: UTMParams = {}

  let hasAnyUtm = false
  for (const key of UTM_KEYS) {
    const value = urlParams.get(key)
    if (value) {
      utmParams[key] = value
      hasAnyUtm = true
    }
  }

  // Fallback: read from jt_utm cookie set by Astro landing page
  if (!hasAnyUtm) {
    const cookieUtm = getCookieValue(UTM_COOKIE_NAME)
    if (cookieUtm) {
      try {
        const parsed = JSON.parse(cookieUtm) as UTMParams
        for (const key of UTM_KEYS) {
          if (parsed[key]) {
            utmParams[key] = parsed[key]
            hasAnyUtm = true
          }
        }
      } catch {
        // Invalid cookie value, ignore
      }
    }
  }

  if (hasAnyUtm) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams))

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log('[UTM Capture]', utmParams)
    }
  }
}

/**
 * Get stored UTM parameters
 * Returns empty object if no UTM params were captured
 */
export const getStoredUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') return {}

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * Clear stored UTM parameters (e.g., after successful conversion)
 */
export const clearUTMParams = (): void => {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(UTM_STORAGE_KEY)
}

/**
 * Get UTM params formatted for GA4 event parameters
 * Flattens utm_* keys to GA4-friendly format
 */
export const getUTMForGA4 = (): Record<string, string> => {
  const params = getStoredUTMParams()
  const ga4Params: Record<string, string> = {}

  if (params.utm_source) ga4Params.campaign_source = params.utm_source
  if (params.utm_medium) ga4Params.campaign_medium = params.utm_medium
  if (params.utm_campaign) ga4Params.campaign_name = params.utm_campaign
  if (params.utm_term) ga4Params.campaign_term = params.utm_term
  if (params.utm_content) ga4Params.campaign_content = params.utm_content

  return ga4Params
}

/**
 * Get UTM params for inclusion in any tracking event
 */
export const getUTMForTracking = (): UTMParams => {
  return getStoredUTMParams()
}
